import { useEffect, useState } from "react";
import { Plus, Trash2, Loader2, Image as ImageIcon, ExternalLink, X, ArrowUp, ArrowDown, Clock } from "lucide-react";
import { toast } from "sonner";
import { adminApi, formatApiError } from "@/lib/api";
import { compressImage } from "@/lib/imageCompress";
import { ADMIN } from "@/constants/testIds";

const PLACEMENTS = [
  { value: "all", label: "Em todas as telas" },
  { value: "radio", label: "Somente Rádio" },
  { value: "tv", label: "Somente TV" },
  { value: "home", label: "Home / Cidadão" },
];

const CONTACT_KINDS = [
  { value: "whatsapp", label: "WhatsApp", placeholder: "5519999999999 (com DDI+DDD, só números)" },
  { value: "instagram", label: "Instagram", placeholder: "usuario (sem @)" },
  { value: "facebook", label: "Facebook", placeholder: "usuario ou URL completa" },
  { value: "phone", label: "Telefone", placeholder: "(19) 3333-4444" },
  { value: "email", label: "E-mail", placeholder: "contato@empresa.com" },
  { value: "url", label: "Site", placeholder: "https://empresa.com.br" },
];

const empty = {
  title: "",
  sponsor: "",
  description: "",
  image_url: "",
  target_url: "",
  placement: "all",
  active: true,
  start_date: null,
  end_date: null,
  contacts: [],
  in_carousel: true,
  duration_seconds: 6,
};

const AdminAds = () => {
  const [ads, setAds] = useState([]);
  const [busy, setBusy] = useState(false);
  const [form, setForm] = useState(empty);
  const [showForm, setShowForm] = useState(false);
  const [editingId, setEditingId] = useState(null);

  const load = async () => {
    try {
      const { data } = await adminApi.get("/admin/ads");
      setAds(data);
    } catch {}
  };

  useEffect(() => {
    load();
  }, []);

  const onFile = (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    compressImage(f).catch(() => f).then((img) => {
      if (img.size > 1.5 * 1024 * 1024) { toast.error("Imagem muito grande mesmo após compressão (máx 1,5 MB)."); return; }
      const reader = new FileReader();
      reader.onload = () => setForm((s) => ({ ...s, image_url: String(reader.result || "") }));
      reader.readAsDataURL(img);
    });
  };

  const addContact = () => {
    setForm((f) => ({
      ...f,
      contacts: [...(f.contacts || []), { kind: "whatsapp", label: "", value: "" }],
    }));
  };

  const updateContact = (i, patch) => {
    setForm((f) => ({
      ...f,
      contacts: f.contacts.map((c, idx) => (idx === i ? { ...c, ...patch } : c)),
    }));
  };

  const removeContact = (i) => {
    setForm((f) => ({
      ...f,
      contacts: f.contacts.filter((_, idx) => idx !== i),
    }));
  };

  const startEdit = (ad) => {
    setEditingId(ad.id);
    setForm({
      title: ad.title || "",
      sponsor: ad.sponsor || "",
      description: ad.description || "",
      image_url: ad.image_url || "",
      target_url: ad.target_url || "",
      placement: ad.placement || "all",
      active: ad.active,
      start_date: ad.start_date || null,
      end_date: ad.end_date || null,
      contacts: ad.contacts || [],
      in_carousel: ad.in_carousel !== false,
      duration_seconds: ad.duration_seconds || 6,
    });
    setShowForm(true);
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  const resetForm = () => {
    setForm(empty);
    setEditingId(null);
    setShowForm(false);
  };

  const onSubmit = async (e) => {
    e.preventDefault();
    if (!form.title || !form.sponsor || !form.image_url) {
      toast.error("Preencha título, patrocinador e imagem.");
      return;
    }
    // Filter empty contacts
    const payload = {
      ...form,
      contacts: (form.contacts || []).filter((c) => c.value && c.value.trim()),
    };
    setBusy(true);
    try {
      if (editingId) {
        await adminApi.patch(`/admin/ads/${editingId}`, payload);
        toast.success("Anúncio atualizado.");
      } else {
        await adminApi.post("/admin/ads", payload);
        toast.success("Anúncio criado.");
      }
      resetForm();
      load();
    } catch (e) {
      toast.error(formatApiError(e?.response?.data?.detail));
    } finally {
      setBusy(false);
    }
  };

  const toggle = async (id) => {
    try {
      await adminApi.post(`/admin/ads/${id}/toggle`);
      load();
    } catch {}
  };

  const remove = async (id) => {
    if (!window.confirm("Remover este anúncio?")) return;
    try {
      await adminApi.delete(`/admin/ads/${id}`);
      toast.success("Removido.");
      if (editingId === id) resetForm();
      load();
    } catch {}
  };

  const move = async (id, direction) => {
    try {
      await adminApi.post(`/admin/ads/${id}/move?direction=${direction}`);
      load();
    } catch {}
  };

  return (
    <div>
      <div className="flex items-start justify-between flex-wrap gap-3">
        <div>
          <div className="text-xs uppercase tracking-[0.28em] text-[var(--fi-red)] font-bold mb-1">Publicidade</div>
          <h1 className="font-display text-4xl sm:text-5xl font-black uppercase text-[var(--fi-blue)] leading-tight">Anúncios</h1>
          <p className="text-gray-500 mt-1">Gerencie os banners de patrocinadores do app.</p>
        </div>
        <button
          data-testid={ADMIN.ads.newBtn}
          onClick={() => (showForm ? resetForm() : setShowForm(true))}
          className="inline-flex items-center gap-2 px-4 py-2.5 rounded-xl bg-[var(--fi-red)] hover:bg-[var(--fi-red-hover)] text-white font-bold uppercase tracking-widest text-xs"
        >
          <Plus className="w-4 h-4" />
          {showForm ? "Fechar" : "Novo anúncio"}
        </button>
      </div>

      {showForm && (
        <form onSubmit={onSubmit} className="mt-5 rounded-xl bg-white p-5 shadow-[0_8px_30px_rgba(13,27,75,0.06)] space-y-3">
          <h2 className="font-display text-xl font-extrabold uppercase text-[var(--fi-blue)] mb-1">
            {editingId ? "Editar anúncio" : "Novo anúncio"}
          </h2>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <Labeled label="Título *">
              <input data-testid={ADMIN.ads.formTitle} value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} className="input" placeholder="Ex: Promoção da semana" />
            </Labeled>
            <Labeled label="Patrocinador *">
              <input data-testid={ADMIN.ads.formSponsor} value={form.sponsor} onChange={(e) => setForm({ ...form, sponsor: e.target.value })} className="input" placeholder="Ex: Padaria Central" />
            </Labeled>
          </div>
          <Labeled label="Descrição curta (aparece no card do anúncio)">
            <textarea value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} rows={2} className="input" placeholder="Ex: Todos os pães saindo do forno das 6h às 22h. Delivery grátis acima de R$30." maxLength={400} />
          </Labeled>
          <Labeled label="Imagem do banner *">
            <label data-testid={ADMIN.ads.formImage} className="flex items-center gap-3 rounded-xl border border-dashed border-gray-300 hover:border-[var(--fi-blue)] p-4 cursor-pointer transition-colors">
              <ImageIcon className="w-5 h-5 text-[var(--fi-blue)]" />
              <span className="text-sm text-gray-600">
                {form.image_url ? "Trocar imagem" : "Selecionar imagem (reduzida automaticamente, use paisagem 16:9)"}
              </span>
              <input type="file" accept="image/*" onChange={onFile} className="hidden" />
            </label>
            {form.image_url && <img src={form.image_url} alt="preview" className="mt-2 rounded-lg h-32 w-full object-cover" />}
          </Labeled>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <Labeled label="URL de 'Saiba mais' (opcional)">
              <input data-testid={ADMIN.ads.formTarget} value={form.target_url} onChange={(e) => setForm({ ...form, target_url: e.target.value })} className="input" placeholder="https://..." />
            </Labeled>
            <Labeled label="Onde exibir">
              <select data-testid={ADMIN.ads.formPlacement} value={form.placement} onChange={(e) => setForm({ ...form, placement: e.target.value })} className="input bg-white">
                {PLACEMENTS.map((p) => <option key={p.value} value={p.value}>{p.label}</option>)}
              </select>
            </Labeled>
          </div>

          {/* Carousel settings */}
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 p-3 rounded-lg bg-blue-50/50 border border-blue-100">
            <Labeled label="Aparecer no carrossel do app público?">
              <label className="flex items-center gap-2 h-10">
                <input
                  type="checkbox"
                  data-testid="admin-ad-in-carousel"
                  checked={form.in_carousel}
                  onChange={(e) => setForm({ ...form, in_carousel: e.target.checked })}
                  className="w-4 h-4 accent-[var(--fi-red)]"
                />
                <span className="text-sm">Incluir no carrossel</span>
              </label>
            </Labeled>
            <Labeled label={`Tempo de exibição: ${form.duration_seconds}s`}>
              <div className="flex items-center gap-2 h-10">
                <Clock className="w-4 h-4 text-gray-400" />
                <input
                  data-testid="admin-ad-duration"
                  type="range"
                  min={3}
                  max={15}
                  value={form.duration_seconds}
                  onChange={(e) => setForm({ ...form, duration_seconds: parseInt(e.target.value, 10) })}
                  className="flex-1 accent-[var(--fi-red)]"
                />
                <span className="text-xs font-bold text-[var(--fi-blue)] w-10 text-right">{form.duration_seconds}s</span>
              </div>
            </Labeled>
          </div>

          {/* Dynamic contacts */}
          <div>
            <div className="flex items-center justify-between mb-2">
              <label className="block text-xs uppercase tracking-widest font-bold text-[var(--fi-blue)]">
                Botões de contato ({(form.contacts || []).length})
              </label>
              <button type="button" onClick={addContact} data-testid="admin-ad-add-contact" className="text-xs uppercase tracking-widest font-bold text-[var(--fi-red)] hover:underline">
                + Adicionar botão
              </button>
            </div>
            <div className="text-xs text-gray-500 mb-2">
              Cada botão gera um CTA no anúncio (WhatsApp abre conversa, Instagram abre perfil, etc).
            </div>
            <div className="space-y-2">
              {(form.contacts || []).map((c, i) => {
                const k = CONTACT_KINDS.find((x) => x.value === c.kind) || CONTACT_KINDS[0];
                return (
                  <div key={i} className="grid grid-cols-1 sm:grid-cols-[140px_1fr_auto] gap-2 items-start p-3 bg-gray-50 rounded-lg">
                    <select
                      value={c.kind}
                      onChange={(e) => updateContact(i, { kind: e.target.value })}
                      className="input bg-white"
                      data-testid={`admin-ad-contact-${i}-kind`}
                    >
                      {CONTACT_KINDS.map((o) => (<option key={o.value} value={o.value}>{o.label}</option>))}
                    </select>
                    <input
                      value={c.value}
                      onChange={(e) => updateContact(i, { value: e.target.value })}
                      className="input"
                      placeholder={k.placeholder}
                      data-testid={`admin-ad-contact-${i}-value`}
                    />
                    <button
                      type="button"
                      onClick={() => removeContact(i)}
                      className="w-10 h-10 rounded-lg text-gray-400 hover:text-[var(--fi-red)] hover:bg-red-50 flex items-center justify-center"
                      aria-label="Remover botão"
                    >
                      <X className="w-4 h-4" />
                    </button>
                  </div>
                );
              })}
            </div>
          </div>

          <div className="flex items-center gap-2 pt-2">
            <button data-testid={ADMIN.ads.formSubmit} disabled={busy} className="inline-flex items-center justify-center gap-2 rounded-xl bg-[var(--fi-blue)] text-white font-bold uppercase tracking-widest text-xs px-5 py-3 hover:bg-[var(--fi-blue-2)] transition-colors disabled:opacity-60">
              {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : editingId ? "Salvar alterações" : "Publicar anúncio"}
            </button>
            {editingId && (
              <button type="button" onClick={resetForm} className="text-xs uppercase tracking-widest font-bold text-gray-500 hover:text-[var(--fi-blue)]">
                Cancelar
              </button>
            )}
          </div>
          <style>{`.input{width:100%;padding:10px 12px;border:1px solid #e5e7eb;border-radius:10px;font-size:14px;outline:none;transition:border-color .15s;font-family:inherit}.input:focus{border-color:#0D1B4B}`}</style>
        </form>
      )}

      <div className="mt-6 space-y-3">
        {ads.length === 0 && <div className="text-center py-16 text-gray-400 font-medium">Nenhum anúncio cadastrado.</div>}
        {ads.map((ad, i) => (
          <div key={ad.id} data-testid={ADMIN.ads.row(ad.id)} className="rounded-xl bg-white shadow-[0_6px_20px_rgba(13,27,75,0.05)] p-4 flex items-center gap-4">
            <div className="flex flex-col">
              <button
                onClick={() => move(ad.id, "up")}
                disabled={i === 0}
                data-testid={`admin-ad-up-${ad.id}`}
                className="w-6 h-6 rounded text-gray-400 hover:text-[var(--fi-blue)] disabled:opacity-30 flex items-center justify-center"
                aria-label="Subir na ordem"
              >
                <ArrowUp className="w-3.5 h-3.5" />
              </button>
              <button
                onClick={() => move(ad.id, "down")}
                disabled={i === ads.length - 1}
                data-testid={`admin-ad-down-${ad.id}`}
                className="w-6 h-6 rounded text-gray-400 hover:text-[var(--fi-blue)] disabled:opacity-30 flex items-center justify-center"
                aria-label="Descer na ordem"
              >
                <ArrowDown className="w-3.5 h-3.5" />
              </button>
            </div>
            <img src={ad.image_url} alt="" className="w-24 h-24 rounded-lg object-cover flex-shrink-0" />
            <div className="flex-1 min-w-0">
              <div className="text-[10px] uppercase tracking-widest font-bold text-[var(--fi-red)]">{ad.sponsor}</div>
              <div className="font-display text-lg font-extrabold text-[var(--fi-blue)] leading-tight truncate">{ad.title}</div>
              <div className="text-xs text-gray-500 mt-1 flex flex-wrap gap-3">
                <span>{ad.impressions.toLocaleString("pt-BR")} impressões</span>
                <span>{ad.clicks.toLocaleString("pt-BR")} cliques</span>
                <span>Onde: <b>{ad.placement}</b></span>
                <span>Botões: <b>{(ad.contacts || []).length}</b></span>
                <span className="inline-flex items-center gap-1"><Clock className="w-3 h-3" /> {ad.duration_seconds || 6}s</span>
                <span className={ad.in_carousel === false ? "text-amber-600 font-bold" : "text-emerald-600 font-bold"}>
                  {ad.in_carousel === false ? "Fora do carrossel" : "No carrossel"}
                </span>
              </div>
            </div>
            <button
              data-testid={ADMIN.ads.toggle(ad.id)}
              onClick={() => toggle(ad.id)}
              className={`px-3 py-1.5 rounded-full text-xs uppercase tracking-widest font-bold transition-colors ${
                ad.active ? "bg-emerald-500 text-white hover:bg-emerald-600" : "bg-gray-200 text-gray-600 hover:bg-gray-300"
              }`}
            >
              {ad.active ? "Ativo" : "Pausado"}
            </button>
            <button onClick={() => startEdit(ad)} data-testid={`admin-ad-edit-${ad.id}`} className="px-3 py-1.5 rounded-lg text-xs uppercase tracking-widest font-bold text-[var(--fi-blue)] hover:bg-gray-100">
              Editar
            </button>
            <button data-testid={ADMIN.ads.delete(ad.id)} onClick={() => remove(ad.id)} className="w-9 h-9 rounded-lg text-gray-400 hover:text-[var(--fi-red)] hover:bg-red-50 flex items-center justify-center transition-colors" aria-label="Remover">
              <Trash2 className="w-4 h-4" />
            </button>
          </div>
        ))}
      </div>
    </div>
  );
};

const Labeled = ({ label, children }) => (
  <div>
    <label className="block text-xs uppercase tracking-widest font-bold text-[var(--fi-blue)] mb-1.5">{label}</label>
    {children}
  </div>
);

export default AdminAds;
